import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import {
  isValidGstin,
  slugify,
  toMajor,
  toMinor,
  type Category,
  type Vendor,
  type VendorRateEntry,
} from '@eprintsmart/shared';
import {
  saveVendor,
  setDefaultVendor,
  subscribeToDefaultVendor,
  subscribeToOpenJobCounts,
  subscribeToVendors,
} from '../services/orders';
import { subscribeToCategories } from '../services/catalog';
import { inviteVendor } from '../services/notifications';
import { VendorSettlementDialog } from '../components/VendorSettlementDialog';

export function Vendors() {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [jobCounts, setJobCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<Vendor> | null>(null);
  const [defaultVendorId, setDefaultVendorId] = useState<string | null>(null);
  const [inviting, setInviting] = useState<string | null>(null);
  const [invited, setInvited] = useState<Set<string>>(new Set());
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [settling, setSettling] = useState<Vendor | null>(null);

  const invite = async (vendorId: string) => {
    setInviting(vendorId);
    setInviteError(null);
    try {
      await inviteVendor(vendorId);
      setInvited((prev) => new Set(prev).add(vendorId));
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Could not send that invite.');
    } finally {
      setInviting(null);
    }
  };

  useEffect(() => subscribeToVendors((next) => { setVendors(next); setLoading(false); }), []);
  useEffect(() => subscribeToCategories(setCategories), []);
  useEffect(() => subscribeToOpenJobCounts(setJobCounts), []);
  useEffect(() => subscribeToDefaultVendor(setDefaultVendorId), []);

  return (
    <Stack spacing={2}>
      {inviteError && (
        <Alert severity="error" onClose={() => setInviteError(null)}>{inviteError}</Alert>
      )}
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h1" sx={{ flexGrow: 1 }}>Vendors</Typography>
        <Button
          variant="contained"
          onClick={() => setEditing({ status: 'ACTIVE', turnaroundDays: 3, capabilities: [], rateCard: [] })}
        >
          Add vendor
        </Button>
      </Stack>

      {/*
        * Who new orders go to, stated on the page rather than buried in
        * Firestore. An order that reaches artwork review is routed here
        * automatically; with nothing set it waits for a human, which is how
        * the first paid order sat untouched for days with no error anywhere.
        */}
      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Stack sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2">Where new orders go</Typography>
            <Typography variant="caption" color="text.secondary">
              Assigned automatically once artwork is in. You can reassign any line afterwards.
            </Typography>
          </Stack>
          <TextField
            select
            size="small"
            label="Default"
            sx={{ minWidth: 260 }}
            value={defaultVendorId ?? ''}
            onChange={(e) => void setDefaultVendor(e.target.value)}
          >
            {vendors
              .filter((v) => v.status === 'ACTIVE')
              .map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.name}{v.isInHouse ? ' — in-house' : ''}
                </MenuItem>
              ))}
          </TextField>
        </Stack>
        {!defaultVendorId && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No default set. Paid orders will wait to be assigned by hand, and nothing
            will tell you they are waiting.
          </Alert>
        )}
      </Paper>

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>
      ) : vendors.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No vendors yet. Orders cannot be assigned until at least one exists.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Vendor</TableCell>
                <TableCell>Produces</TableCell>
                <TableCell align="right">Turnaround</TableCell>
                <TableCell align="right">Rates</TableCell>
                <TableCell align="right">Open jobs</TableCell>
                <TableCell align="right">On time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Invite</TableCell>
                <TableCell align="right">Pay</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vendors.map((v) => (
                <TableRow key={v.id} hover sx={{ cursor: 'pointer' }} onClick={() => setEditing(v)}>
                  <TableCell>
                    {v.name}
                    {/* Two different facts, deliberately not merged into one
                        chip: one says this is us rather than a supplier, the
                        other says new orders land here. They are usually the
                        same vendor and do not have to be. */}
                    {v.isInHouse && (
                      <Chip size="small" label="in-house" color="info" sx={{ ml: 1 }} />
                    )}
                    {v.id === defaultVendorId && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="default"
                        color="success"
                        sx={{ ml: 0.5 }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {v.contactPerson} · {v.phone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {v.capabilities.length === 0 ? (
                      <Chip size="small" label="none set" color="warning" variant="outlined" />
                    ) : (
                      <Typography variant="caption">{v.capabilities.length} categories</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{v.turnaroundDays}d</TableCell>
                  <TableCell align="right">{v.rateCard.length}</TableCell>
                  <TableCell align="right">{jobCounts[v.id] ?? 0}</TableCell>
                  <TableCell align="right">
                    {v.onTimeRate != null ? `${Math.round(v.onTimeRate * 100)}%` : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={v.status}
                      color={v.status === 'ACTIVE' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {/* Explicit, and never automatic on creating the record —
                        rate cards get entered while the commercial conversation
                        is still going on, and emailing a press the moment a row
                        appears would be sending on their behalf uninvited.
                        stopPropagation because the row itself opens the editor. */}
                    <Button
                      size="small"
                      disabled={!v.email || inviting === v.id}
                      title={v.email ? `Send to ${v.email}` : 'No email address on file'}
                      onClick={(e) => { e.stopPropagation(); void invite(v.id); }}
                    >
                      {invited.has(v.id) ? 'Sent' : 'Invite'}
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    {/* stopPropagation: the row itself opens the editor. */}
                    <Button size="small"
                      onClick={(e) => { e.stopPropagation(); setSettling(v); }}>
                      Statement
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <VendorDialog
        draft={editing}
        categories={categories}
        onClose={() => setEditing(null)}
      />

      {settling && (
        <VendorSettlementDialog vendor={settling} onClose={() => setSettling(null)} />
      )}
    </Stack>
  );
}

function VendorDialog({
  draft,
  categories,
  onClose,
}: {
  draft: Partial<Vendor> | null;
  categories: Category[];
  onClose: () => void;
}) {
  const [form, setForm] = useState<Partial<Vendor>>({});
  const [rates, setRates] = useState<VendorRateEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(draft ?? {});
    setRates(draft?.rateCard ?? []);
    setError(null);
  }, [draft]);

  // Only leaf and mid-level categories are worth quoting against; a vendor
  // "producing" a whole top-level group is too coarse to price.
  const assignable = useMemo(
    () => categories.filter((c) => c.level > 0).sort((a, b) => a.name.localeCompare(b.name)),
    [categories],
  );

  if (!draft) return null;
  const isNew = !draft.id;

  const set = <K extends keyof Vendor>(key: K, value: Vendor[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setError(null);
    const name = (form.name ?? '').trim();
    if (name.length < 2) return setError('A vendor name is required.');
    if (!/^[6-9]\d{9}$/.test(form.phone ?? '')) return setError('Enter a 10-digit Indian mobile number.');
    if (form.gstin && !isValidGstin(form.gstin)) return setError('That GSTIN does not look valid.');
    if ((form.capabilities ?? []).length === 0) {
      return setError('Choose at least one category, or this vendor can never be suggested.');
    }

    setSaving(true);
    try {
      await saveVendor(draft.id ?? `vendor_${slugify(name)}`, {
        ...form,
        name,
        rateCard: rates.filter((r) => r.targetId && r.unitCostMinor > 0),
        ...(isNew ? { createdAt: Date.now() } : {}),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isNew ? 'New vendor' : form.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" spacing={2}>
            <TextField label="Business name" value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} sx={{ flex: 2 }} />
            <TextField label="Contact person" value={form.contactPerson ?? ''} onChange={(e) => set('contactPerson', e.target.value)} sx={{ flex: 1 }} />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField label="Phone" value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value.replace(/\D/g, ''))} inputProps={{ maxLength: 10 }} sx={{ flex: 1 }} />
            <TextField label="Email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} sx={{ flex: 1 }} />
            <TextField label="GSTIN" value={form.gstin ?? ''} onChange={(e) => set('gstin', e.target.value.toUpperCase())} sx={{ flex: 1 }} />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Turnaround (working days)"
              type="number"
              value={form.turnaroundDays ?? 3}
              onChange={(e) => set('turnaroundDays', Number(e.target.value))}
              sx={{ width: 220 }}
              helperText="Used to check they can make the promised date."
            />
            <TextField
              label="Status"
              select
              value={form.status ?? 'ACTIVE'}
              onChange={(e) => set('status', e.target.value as Vendor['status'])}
              sx={{ width: 180 }}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="PAUSED">Paused</MenuItem>
              <MenuItem value="ARCHIVED">Archived</MenuItem>
            </TextField>
          </Stack>

          {/* Not cosmetic: an in-house job is costed at zero, because there is
              no bill to pay ourselves, and is kept out of settlements and
              scorecards for the same reason. */}
          <FormControlLabel
            control={
              <Checkbox
                checked={form.isInHouse ?? false}
                onChange={(e) => set('isInHouse', e.target.checked)}
              />
            }
            label="This is our own shop, not a supplier"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1, ml: 4 }}>
            In-house jobs cost nothing to buy in, so they are priced at zero and left out
            of settlements and on-time scoring.
          </Typography>

          <TextField
            label="Produces these categories"
            select
            SelectProps={{ multiple: true }}
            value={form.capabilities ?? []}
            onChange={(e) =>
              set('capabilities', (typeof e.target.value === 'string'
                ? e.target.value.split(',')
                : e.target.value) as string[])
            }
            helperText="A vendor is only ever suggested for categories listed here."
          >
            {assignable.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </TextField>

          <Box>
            <Typography variant="subtitle2" gutterBottom>Rate card</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              What we pay this vendor, per unit. A product rate always beats a category rate.
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Applies to</TableCell>
                  <TableCell width={130}>Type</TableCell>
                  <TableCell width={140}>Unit cost (₹)</TableCell>
                  <TableCell width={120}>Min qty</TableCell>
                  <TableCell width={48} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rates.map((rate, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <TextField
                        size="small" fullWidth placeholder="category or product id"
                        value={rate.targetId}
                        onChange={(e) => setRates((r) => r.map((x, j) => j === i ? { ...x, targetId: e.target.value } : x))}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small" select fullWidth value={rate.targetType}
                        onChange={(e) => setRates((r) => r.map((x, j) => j === i ? { ...x, targetType: e.target.value as 'product' | 'category' } : x))}
                      >
                        <MenuItem value="category">Category</MenuItem>
                        <MenuItem value="product">Product</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small" type="number" fullWidth
                        value={toMajor(rate.unitCostMinor)}
                        onChange={(e) => setRates((r) => r.map((x, j) => j === i ? { ...x, unitCostMinor: toMinor(Number(e.target.value)) } : x))}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small" type="number" fullWidth value={rate.minQty ?? ''}
                        onChange={(e) => setRates((r) => r.map((x, j) => j === i ? { ...x, minQty: e.target.value ? Number(e.target.value) : undefined } : x))}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => setRates((r) => r.filter((_, j) => j !== i))}>
                        <DeleteOutlineIcon fontSize="inherit" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Button
              size="small"
              startIcon={<AddIcon />}
              sx={{ mt: 1 }}
              onClick={() => setRates((r) => [...r, { targetId: '', targetType: 'category', unitCostMinor: 0 }])}
            >
              Add rate
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Save vendor</Button>
      </DialogActions>
    </Dialog>
  );
}
